import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  LayoutDashboard, Mountain, CalendarCheck, Users, Mail, FileText, LogOut, Plus, Trash2,
  Pencil, X, ExternalLink, Inbox, AlertTriangle, Wallet, ChevronRight, Check, Clock, FlaskConical, Ticket,
  Upload, Star, GripVertical, ArrowUp, ArrowDown, ImagePlus, Send, MailCheck, Eye, RotateCcw, Compass,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { useAdmin } from "./AdminContext";
import AdminLogin from "./AdminLogin";

const REGIONS = ["Afrique", "Asie", "Amérique", "Arctique", "Océanie", "Océan", "Europe"];
const ENVIRONMENTS = ["montagne", "désert", "jungle", "mer", "glace", "plaine"];
const RES_STATUS = ["Demande reçue", "À examiner", "Informations demandées", "Acceptée", "Refusée", "Paiement en attente", "Option expirée", "Acompte payé", "Confirmée", "Solde à payer", "Payée intégralement", "Annulée", "Remboursée"];
const PAY_MODES = ["Virement", "Carte bancaire", "Espèces", "Stripe", "Chèque"];

const linesToArr = (t) => (t || "").split("\n").map((l) => l.trim()).filter(Boolean);
const arrToLines = (a) => (Array.isArray(a) ? a.join("\n") : "");
const datesToLines = (dates, caps) => (Array.isArray(dates) ? dates : []).map((d) => { const dd = String(d).slice(0, 10); const c = caps && caps[dd]; return c ? `${d}, ${c}` : `${d}`; }).join("\n");
const linesToDatesCaps = (text) => { const dates = []; const caps = {}; (text || "").split("\n").map((l) => l.trim()).filter(Boolean).forEach((l) => { const [d, c] = l.split(",").map((s) => (s || "").trim()); if (!d) return; dates.push(d); const n = parseInt(c, 10); if (!isNaN(n) && n > 0) caps[d.slice(0, 10)] = n; }); return { dates, caps }; };
const arrLoc = (a) => arrToLines(Array.isArray(a) ? a : (a && a.fr) || []);
const faqToLines = (a) => (Array.isArray(a) ? a.map((f) => `${f.q} :: ${f.a}`).join("\n") : "");
const linesToFaq = (t) => linesToArr(t).map((l) => { const [q, ...r] = l.split("::"); return { q: (q || "").trim(), a: r.join("::").trim() }; });
const L = (v) => (typeof v === "object" && v ? v.fr : v);
const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR")} €`;
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const fmtD = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); } catch { return iso || "—"; } };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
const inp = "w-full bg-[#0F0F0F] border border-white/15 px-3 py-2 text-sm text-[#F5F5F0] outline-none focus:border-[#BC5D2E]";
const TestBadge = () => <span className="font-mono-hs text-[9px] uppercase tracking-widest bg-[#8A281E]/20 text-[#e88] border border-[#8A281E]/40 px-1.5 py-0.5" data-testid="test-badge">TEST</span>;

const EXP_STATUS = ["draft", "published", "coming_soon", "Départ confirmé", "Dernières places", "Sold Out", "Terminée", "Annulée"];
const EXP_STATUS_LABEL = { draft: "Brouillon", published: "Publié (disponible)", coming_soon: "Coming Soon (prévisualisation)" };
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const imgSrc = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

/* Bloc éditable réordonnable (drag-drop) */
function BlockList({ label, items, onChange, testid }) {
  const list = Array.isArray(items) ? items : [];
  const [val, setVal] = useState("");
  const [drag, setDrag] = useState(null);
  const add = () => { if (!val.trim()) return; onChange([...list, val.trim()]); setVal(""); };
  const edit = (i, v) => { const n = [...list]; n[i] = v; onChange(n); };
  const del = (i) => onChange(list.filter((_, j) => j !== i));
  const move = (from, to) => { if (to < 0 || to >= list.length) return; const n = [...list]; const [x] = n.splice(from, 1); n.splice(to, 0, x); onChange(n); };
  return (
    <div data-testid={testid}>
      <span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">{label}</span>
      <div className="mt-1 space-y-1">
        {list.map((it, i) => (
          <div key={i} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (drag !== null) move(drag, i); setDrag(null); }}
               className="flex items-center gap-1 bg-[#0F0F0F] border border-white/10 px-2 py-1" data-testid={`${testid}-item`}>
            <GripVertical size={13} className="text-[#555] cursor-grab shrink-0" />
            <input className="flex-1 bg-transparent text-sm text-[#F5F5F0] outline-none" value={it} onChange={(e) => edit(i, e.target.value)} />
            <button type="button" onClick={() => move(i, i - 1)} className="text-[#666] hover:text-white"><ArrowUp size={13} /></button>
            <button type="button" onClick={() => move(i, i + 1)} className="text-[#666] hover:text-white"><ArrowDown size={13} /></button>
            <button type="button" onClick={() => del(i)} className="text-[#666] hover:text-[#8A281E]"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-1">
        <input className="flex-1 bg-[#0F0F0F] border border-white/15 px-2 py-1.5 text-sm text-[#F5F5F0] outline-none focus:border-[#BC5D2E]" placeholder="Nouvel élément…" value={val}
               onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid={`${testid}-input`} />
        <button type="button" onClick={add} className="bg-[#BC5D2E] text-white px-3 hover:bg-[#8A281E]" data-testid={`${testid}-add`}><Plus size={16} /></button>
      </div>
    </div>
  );
}

/* Éditeur de départs : début / fin / participants max, réordonnable (comme "Inclus") */
function DeparturesEditor({ items, onChange, defaultMax = 5 }) {
  const list = Array.isArray(items) ? items : [];
  const [drag, setDrag] = useState(null);
  const add = () => onChange([...list, { start: "", end: "", max: defaultMax }]);
  const edit = (i, k, v) => { const n = [...list]; n[i] = { ...n[i], [k]: v }; onChange(n); };
  const del = (i) => onChange(list.filter((_, j) => j !== i));
  const move = (from, to) => { if (to < 0 || to >= list.length) return; const n = [...list]; const [x] = n.splice(from, 1); n.splice(to, 0, x); onChange(n); };
  const days = (s, e) => { if (!s || !e) return null; return Math.round((new Date(e) - new Date(s)) / 86400000) + 1; };
  const fieldCls = "w-full bg-transparent border border-white/15 px-2 py-1 text-sm text-[#F5F5F0] outline-none focus:border-[#BC5D2E]";
  const lbl = "block font-mono-hs text-[9px] uppercase tracking-widest text-[#666] mb-0.5";
  return (
    <div data-testid="exp-departures">
      <span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">Dates de départ (chaque départ gère ses propres places)</span>
      <div className="mt-1 space-y-2">
        {list.map((it, i) => {
          const d = days(it.start, it.end);
          return (
            <div key={i} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (drag !== null) move(drag, i); setDrag(null); }}
                 className="bg-[#0F0F0F] border border-white/10 p-2" data-testid="exp-departures-item">
              <div className="flex items-start gap-2">
                <GripVertical size={13} className="text-[#555] cursor-grab shrink-0 mt-5" />
                <div className="grid grid-cols-3 gap-2 flex-1">
                  <div><label className={lbl}>Début</label><input type="date" className={fieldCls} value={it.start || ""} onChange={(e) => edit(i, "start", e.target.value)} data-testid="dep-start" /></div>
                  <div><label className={lbl}>Fin</label><input type="date" className={fieldCls} value={it.end || ""} onChange={(e) => edit(i, "end", e.target.value)} data-testid="dep-end" /></div>
                  <div><label className={lbl}>Participants max</label><input type="number" min="1" className={fieldCls} value={it.max ?? defaultMax} onChange={(e) => edit(i, "max", Number(e.target.value))} data-testid="dep-max" /></div>
                </div>
                <div className="flex flex-col mt-4">
                  <button type="button" onClick={() => move(i, i - 1)} className="text-[#666] hover:text-white"><ArrowUp size={13} /></button>
                  <button type="button" onClick={() => move(i, i + 1)} className="text-[#666] hover:text-white"><ArrowDown size={13} /></button>
                </div>
                <button type="button" onClick={() => del(i)} className="text-[#666] hover:text-[#8A281E] mt-4" data-testid="dep-del"><Trash2 size={14} /></button>
              </div>
              <div className="font-mono-hs text-[9px] uppercase tracking-widest mt-1 pl-6" data-testid="dep-days">
                {it.start && it.end
                  ? (d && d > 0
                      ? <span className="text-[#BC5D2E]">{d} jour{d > 1 ? "s" : ""}</span>
                      : <span className="text-[#8A281E]">La date de fin doit être après le début</span>)
                  : <span className="text-[#666]">— renseignez début et fin</span>}
              </div>
            </div>
          );
        })}
      </div>
      <button type="button" onClick={add} className="mt-2 inline-flex items-center gap-1.5 bg-[#BC5D2E] text-white px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-[#8A281E]" data-testid="exp-departures-add"><Plus size={14} /> Ajouter une date</button>
    </div>
  );
}


/* Éditeur FAQ (question/réponse) réordonnable */
function FaqEditor({ items, onChange }) {
  const list = Array.isArray(items) ? items : [];
  const [q, setQ] = useState(""); const [a, setA] = useState(""); const [drag, setDrag] = useState(null);
  const add = () => { if (!q.trim()) return; onChange([...list, { q: q.trim(), a: a.trim() }]); setQ(""); setA(""); };
  const edit = (i, k, v) => { const n = [...list]; n[i] = { ...n[i], [k]: v }; onChange(n); };
  const del = (i) => onChange(list.filter((_, j) => j !== i));
  const move = (from, to) => { if (to < 0 || to >= list.length) return; const n = [...list]; const [x] = n.splice(from, 1); n.splice(to, 0, x); onChange(n); };
  return (
    <div data-testid="exp-faq">
      <span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">FAQ</span>
      <div className="mt-1 space-y-2">
        {list.map((it, i) => (
          <div key={i} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (drag !== null) move(drag, i); setDrag(null); }}
               className="bg-[#0F0F0F] border border-white/10 p-2 space-y-1">
            <div className="flex items-center gap-1">
              <GripVertical size={13} className="text-[#555] cursor-grab" />
              <input className="flex-1 bg-transparent text-sm text-[#F5F5F0] outline-none font-medium" value={it.q} onChange={(e) => edit(i, "q", e.target.value)} placeholder="Question" />
              <button type="button" onClick={() => move(i, i - 1)} className="text-[#666] hover:text-white"><ArrowUp size={13} /></button>
              <button type="button" onClick={() => move(i, i + 1)} className="text-[#666] hover:text-white"><ArrowDown size={13} /></button>
              <button type="button" onClick={() => del(i)} className="text-[#666] hover:text-[#8A281E]"><Trash2 size={13} /></button>
            </div>
            <input className="w-full bg-transparent text-sm text-[#A3A3A3] outline-none pl-5" value={it.a} onChange={(e) => edit(i, "a", e.target.value)} placeholder="Réponse" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <input className="bg-[#0F0F0F] border border-white/15 px-2 py-1.5 text-sm outline-none focus:border-[#BC5D2E]" placeholder="Question" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="flex gap-2">
          <input className="flex-1 bg-[#0F0F0F] border border-white/15 px-2 py-1.5 text-sm outline-none focus:border-[#BC5D2E]" placeholder="Réponse" value={a} onChange={(e) => setA(e.target.value)} />
          <button type="button" onClick={add} className="bg-[#BC5D2E] text-white px-3 hover:bg-[#8A281E]"><Plus size={16} /></button>
        </div>
      </div>
    </div>
  );
}

/* Galerie d'images: upload, miniatures, couverture, suppression, réordonnancement */
function ImageManager({ cover, gallery, onChange }) {
  const list = Array.isArray(gallery) ? gallery : [];
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [drag, setDrag] = useState(null);
  const all = [...(cover ? [cover] : []), ...list.filter((u) => u !== cover)];

  const upload = async (files) => {
    setUploading(true);
    let curCover = cover; let curList = [...list.filter((u) => u !== cover)];
    for (const file of files) {
      setProgress(0);
      try {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/admin/upload-image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
        });
        if (!curCover) curCover = data.url; else curList = [...curList, data.url];
        onChange({ cover: curCover, gallery: curList });
        toast.success("Image ajoutée ✓");
      } catch (err) { toast.error(err?.response?.data?.detail || "Échec de l'upload"); }
    }
    setUploading(false); setProgress(0);
  };

  const setCover = (u) => onChange({ cover: u, gallery: all.filter((x) => x !== u) });
  const remove = (u) => {
    const rest = all.filter((x) => x !== u);
    onChange({ cover: rest[0] || "", gallery: rest.slice(1) });
  };
  const reorder = (from, to) => {
    if (to < 0 || to >= all.length) return;
    const n = [...all]; const [x] = n.splice(from, 1); n.splice(to, 0, x);
    onChange({ cover: n[0] || "", gallery: n.slice(1) });
  };

  return (
    <div data-testid="exp-images">
      <span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">Images ({all.length}) — 1re = couverture</span>
      <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
        {all.map((u, i) => (
          <div key={u} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (drag !== null) reorder(drag, i); setDrag(null); }}
               className={`relative group border ${u === cover ? "border-[#BC5D2E]" : "border-white/10"}`} data-testid="exp-image-thumb">
            <img src={imgSrc(u)} alt="" className="w-full h-20 object-cover" />
            {u === cover && <span className="absolute top-1 left-1 font-mono-hs text-[8px] uppercase bg-[#BC5D2E] text-white px-1">Couverture</span>}
            <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
              <button type="button" title="Définir couverture" onClick={() => setCover(u)} className="text-white hover:text-[#BC5D2E]" data-testid="img-set-cover"><Star size={15} /></button>
              <button type="button" title="Reculer" onClick={() => reorder(i, i - 1)} className="text-white hover:text-[#BC5D2E]"><ArrowUp size={15} /></button>
              <button type="button" title="Avancer" onClick={() => reorder(i, i + 1)} className="text-white hover:text-[#BC5D2E]"><ArrowDown size={15} /></button>
              <button type="button" title="Supprimer" onClick={() => remove(u)} className="text-white hover:text-[#8A281E]" data-testid="img-delete"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        <label className="border border-dashed border-white/20 h-20 flex flex-col items-center justify-center cursor-pointer hover:border-[#BC5D2E] text-[#737373]" data-testid="img-upload-label">
          <ImagePlus size={18} /><span className="font-mono-hs text-[8px] uppercase mt-1">Ajouter</span>
          <input type="file" accept=".jpg,.jpeg,.png,.webp" multiple className="hidden" data-testid="img-upload-input"
                 onChange={(e) => { if (e.target.files?.length) upload(Array.from(e.target.files)); e.target.value = ""; }} />
        </label>
      </div>
      {uploading && (
        <div className="mt-2" data-testid="img-progress">
          <div className="h-1 bg-white/10"><div className="h-1 bg-[#BC5D2E] transition-all" style={{ width: `${progress}%` }} /></div>
          <span className="font-mono-hs text-[9px] text-[#737373]">Envoi… {progress}%</span>
        </div>
      )}
    </div>
  );
}


/* ---------------- Expedition Form (CMS multi-lignes) ---------------- */
function ExpeditionForm({ initial, onClose, onSaved }) {
  const empty = {
    title: "", location: "", region: "Afrique", environment: "montagne", tagline: "", description: "",
    intensity: 3, risk: 3, duration_days: 7, price: 0, promo_price: 0, deposit: 0, spots_total: 5, spots_left: 5,
    cover: "", gallery: [], available_dates: [], date_capacities: {}, departures: [], included: [], excluded: [], program: [], gear: [],
    physical: [], safety: [], important_info: [], faq: [], status: "draft", hidden: false,
    best_seller: false, sold_out_manual: false, order: 0, coordinator_ids: [],
  };
  const LOC_STR = ["title", "location", "tagline", "description"];
  const LOC_ARR = ["included", "excluded", "program", "gear"];
  const BLOCK_ARR = ["physical", "safety", "important_info"];
  const normInit = () => {
    const n = { ...empty, ...(initial || {}) };
    LOC_STR.forEach((k) => { if (n[k] && typeof n[k] === "object" && !Array.isArray(n[k])) n[k] = n[k].fr ?? ""; });
    LOC_ARR.forEach((k) => { if (n[k] && !Array.isArray(n[k])) n[k] = n[k].fr ?? []; });
    BLOCK_ARR.forEach((k) => {
      let v = n[k];
      if (v && typeof v === "object" && !Array.isArray(v)) v = v.fr;
      if (typeof v === "string") v = v.trim() ? [v.trim()] : [];
      n[k] = Array.isArray(v) ? v : [];
    });
    // Départs : dérive depuis available_dates + date_capacities si absent
    if (!Array.isArray(n.departures) || n.departures.length === 0) {
      const dts = Array.isArray(n.available_dates) ? n.available_dates : [];
      const caps = (n.date_capacities && typeof n.date_capacities === "object") ? n.date_capacities : {};
      const dur = Number(n.duration_days) || 7;
      n.departures = dts.map((dd) => {
        const s = String(dd).slice(0, 10);
        let end = s;
        try { end = new Date(new Date(s).getTime() + (dur - 1) * 86400000).toISOString().slice(0, 10); } catch (e) {}
        return { start: s, end, max: Number(caps[s]) || Number(n.spots_total) || 5 };
      });
    }
    return n;
  };
  const [f, setF] = useState(normInit);
  const [busy, setBusy] = useState(false);
  const [coordsAll, setCoordsAll] = useState([]);
  useEffect(() => { api.get("/admin/coordinators").then(({ data }) => setCoordsAll(data)).catch(() => {}); }, []);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleCoord = (id) => set("coordinator_ids", (f.coordinator_ids || []).includes(id) ? f.coordinator_ids.filter((x) => x !== id) : [...(f.coordinator_ids || []), id]);

  const buildPayload = () => {
    const out = { ...f };
    [...LOC_STR, ...LOC_ARR].forEach((k) => {
      const orig = initial ? initial[k] : undefined;
      if (orig && typeof orig === "object" && !Array.isArray(orig) && ("fr" in orig || "en" in orig)) {
        out[k] = { fr: f[k], en: orig.en !== undefined ? orig.en : f[k] };
      }
    });
    return out;
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = buildPayload();
      if (initial?.slug) await api.put(`/admin/expeditions/${initial.slug}`, payload);
      else await api.post("/admin/expeditions", payload);
      toast.success("Enregistré ✓"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[90] bg-black/70 overflow-y-auto p-4" onClick={onClose}>
      <div className="max-w-3xl mx-auto bg-[#141414] border border-white/15 p-6 my-8" onClick={(e) => e.stopPropagation()} data-testid="expedition-form">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-clash text-xl uppercase tracking-tight">{initial?.slug ? "Modifier" : "Nouvelle"} expédition</h3>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white" data-testid="exp-form-close"><X /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Titre"><input className={inp} value={L(f.title)} onChange={(e) => set("title", e.target.value)} data-testid="exp-title" /></Field>
          <Field label="Pays / lieu"><input className={inp} value={L(f.location)} onChange={(e) => set("location", e.target.value)} /></Field>
          <Field label="Région (carte)"><select className={inp} value={f.region} onChange={(e) => set("region", e.target.value)}>{REGIONS.map((r) => <option key={r}>{r}</option>)}</select></Field>
          <Field label="Environnement"><select className={inp} value={f.environment} onChange={(e) => set("environment", e.target.value)}>{ENVIRONMENTS.map((r) => <option key={r}>{r}</option>)}</select></Field>
          <Field label="Intensité (1-5)"><input type="number" min="1" max="5" className={inp} value={f.intensity} onChange={(e) => set("intensity", Number(e.target.value))} /></Field>
          <Field label="Risque (1-5)"><input type="number" min="1" max="5" className={inp} value={f.risk} onChange={(e) => set("risk", Number(e.target.value))} /></Field>
          <Field label="Durée (jours)"><input type="number" className={inp} value={f.duration_days} onChange={(e) => set("duration_days", Number(e.target.value))} /></Field>
          <Field label="Prix (€)"><input type="number" className={inp} value={f.price} onChange={(e) => set("price", Number(e.target.value))} data-testid="exp-price" /></Field>
          <Field label="Prix promotionnel (€, 0 = aucun)"><input type="number" className={inp} value={f.promo_price} onChange={(e) => set("promo_price", Number(e.target.value))} data-testid="exp-promo-price" /></Field>
          <Field label="Places par date (défaut)"><input type="number" className={inp} value={f.spots_total} onChange={(e) => set("spots_total", Number(e.target.value))} /></Field>
          <Field label="Latitude"><input type="number" step="0.1" className={inp} value={f.lat || 0} onChange={(e) => set("lat", Number(e.target.value))} /></Field>
          <Field label="Longitude"><input type="number" step="0.1" className={inp} value={f.lng || 0} onChange={(e) => set("lng", Number(e.target.value))} /></Field>
          <Field label="Statut"><select className={inp} value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="exp-status">{EXP_STATUS.map((s) => <option key={s} value={s}>{EXP_STATUS_LABEL[s] || s}</option>)}</select></Field>
          <Field label="Ordre d'affichage"><input type="number" className={inp} value={f.order} onChange={(e) => set("order", Number(e.target.value))} data-testid="exp-order" /></Field>
        </div>
        {initial?.slug && (
          <div className="mt-4 border border-white/10 bg-[#0F0F0F] p-4">
            <Field label="URL de l'expédition (slug)">
              <div className="flex items-center gap-2">
                <span className="font-mono-hs text-xs text-[#737373] shrink-0">/expeditions/</span>
                <input className={inp} value={f.slug || ""} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} data-testid="exp-slug" />
              </div>
            </Field>
            <p className="font-mono-hs text-[10px] text-[#737373] mt-2">Modifier l'URL met à jour les liens automatiquement. L'ancienne URL redirigera vers la nouvelle (SEO préservé).</p>
          </div>
        )}
        <div className="flex flex-wrap gap-6 mt-3">
          <label className="flex items-center gap-2 text-sm text-[#D4D4D4]"><input type="checkbox" checked={f.status === "coming_soon"} onChange={(e) => set("status", e.target.checked ? "coming_soon" : "published")} className="accent-[#BC5D2E]" data-testid="exp-comingsoon" /> <Clock size={14} className="text-[#BC5D2E]" /> Coming Soon (prévisualisation, réservation bloquée)</label>
          <label className="flex items-center gap-2 text-sm text-[#D4D4D4]"><input type="checkbox" checked={!!f.best_seller} onChange={(e) => set("best_seller", e.target.checked)} className="accent-[#BC5D2E]" data-testid="exp-bestseller" /> <Star size={14} className="text-[#BC5D2E]" /> Best Seller (badge + 1re position)</label>
          <label className="flex items-center gap-2 text-sm text-[#D4D4D4]"><input type="checkbox" checked={!!f.sold_out_manual} onChange={(e) => set("sold_out_manual", e.target.checked)} className="accent-[#BC5D2E]" data-testid="exp-soldout" /> Forcer SOLD OUT</label>
        </div>
        <p className="font-mono-hs text-[10px] text-[#BC5D2E] mt-2">L'acompte est calculé automatiquement à 30 % du prix (promo inclus). Une place = 5 max par groupe.</p>
        <div className="mt-4 space-y-5">
          <Field label="Accroche courte"><input className={inp} value={L(f.tagline)} onChange={(e) => set("tagline", e.target.value)} /></Field>
          <Field label="Description longue"><textarea rows={3} className={inp} value={L(f.description)} onChange={(e) => set("description", e.target.value)} /></Field>
          <ImageManager cover={f.cover} gallery={f.gallery} onChange={({ cover, gallery }) => setF((s) => ({ ...s, cover, gallery }))} />
          <DeparturesEditor items={f.departures} onChange={(v) => set("departures", v)} defaultMax={Number(f.spots_total) || 5} />
          <div className="grid sm:grid-cols-2 gap-5">
            <BlockList label="Inclus" items={f.included} onChange={(v) => set("included", v)} testid="exp-included" />
            <BlockList label="Non inclus" items={f.excluded} onChange={(v) => set("excluded", v)} testid="exp-excluded" />
            <BlockList label="Programme (un jour = un bloc)" items={f.program} onChange={(v) => set("program", v)} testid="exp-program" />
            <BlockList label="Matériel requis" items={f.gear} onChange={(v) => set("gear", v)} testid="exp-gear" />
            <BlockList label="Conditions physiques" items={f.physical} onChange={(v) => set("physical", v)} testid="exp-physical" />
            <BlockList label="Risques & sécurité" items={f.safety} onChange={(v) => set("safety", v)} testid="exp-safety" />
          </div>
          <BlockList label="Informations importantes" items={f.important_info} onChange={(v) => set("important_info", v)} testid="exp-important" />
          <div data-testid="exp-coordinators">
            <span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">Coordinateur(s) de cette expédition</span>
            {coordsAll.length === 0 ? (
              <p className="text-[#737373] text-xs mt-1">Aucun coordinateur créé. Ajoutez-en dans l'onglet « Coordinateurs ».</p>
            ) : (
              <div className="mt-2 grid sm:grid-cols-2 gap-2">
                {coordsAll.map((c) => {
                  const on = (f.coordinator_ids || []).includes(c.id);
                  return (
                    <button type="button" key={c.id} onClick={() => toggleCoord(c.id)} data-testid={`exp-coord-${c.id}`}
                      className={`flex items-center gap-3 border px-3 py-2 text-left transition-colors ${on ? "border-[#BC5D2E] bg-[#BC5D2E]/10" : "border-white/10 hover:border-white/30"}`}>
                      {c.photo ? <img src={imgSrc(c.photo)} alt="" className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-[#555]"><Compass size={15} /></div>}
                      <div className="min-w-0"><div className="text-sm truncate">{c.name}</div><div className="font-mono-hs text-[9px] uppercase tracking-widest text-[#737373] truncate">{c.role}</div></div>
                      {on && <Check size={16} className="text-[#BC5D2E] ml-auto shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <FaqEditor items={f.faq} onChange={(v) => set("faq", v)} />
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 border border-white/30 py-3 uppercase tracking-widest text-xs hover:border-white">Annuler</button>
          <button onClick={save} disabled={busy} className="flex-1 bg-[#BC5D2E] text-white py-3 uppercase tracking-widest text-xs hover:bg-[#8A281E] disabled:opacity-50" data-testid="exp-save">{busy ? "..." : "Enregistrer"}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
const Kpi = ({ label, value, accent, testid }) => (
  <div className="border border-white/10 bg-[#141414] p-6" data-testid={testid}>
    <div className={`font-clash text-3xl ${accent ? "text-[#BC5D2E]" : "text-[#F5F5F0]"}`}>{value}</div>
    <div className="font-mono-hs text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-2">{label}</div>
  </div>
);

function Dashboard({ go }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <p className="font-mono-hs text-[#737373]">// chargement...</p>;
  return (
    <div data-testid="admin-dashboard">
      <h2 className="font-clash text-2xl uppercase tracking-tight mb-6">Tableau de bord</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="CA réel encaissé" value={eur(s.ca_reel)} accent testid="kpi-ca-reel" />
        <Kpi label="CA potentiel" value={eur(s.ca_potentiel)} testid="kpi-ca-potentiel" />
        <Kpi label="Reste à encaisser" value={eur(s.reste_a_encaisser)} testid="kpi-reste" />
        <Kpi label="Réservations" value={s.total_reservations} testid="kpi-reservations" />
        <Kpi label="Documents en attente" value={s.docs_pending} testid="kpi-docs" />
        <Kpi label="Paiements en attente" value={s.pending_payments} testid="kpi-pending" />
        <Kpi label="Demandes de contact" value={s.contact_requests} testid="kpi-contacts" />
        <Kpi label="Leads (accès)" value={s.leads} testid="kpi-leads" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <div className="border border-white/10 bg-[#141414] p-6" data-testid="dash-a-traiter">
          <h3 className="font-clash uppercase tracking-tight mb-4 flex items-center gap-2"><Inbox size={16} className="text-[#BC5D2E]" /> À traiter ({s.a_traiter.length})</h3>
          {s.a_traiter.length === 0 ? <p className="text-[#737373] text-sm">Rien à traiter. Tout est à jour.</p> :
            s.a_traiter.map((r) => (
              <button key={r.reservation_id} onClick={() => go("reservations")} className="w-full text-left flex items-center justify-between py-2 border-b border-white/5 hover:text-[#BC5D2E]">
                <div className="min-w-0"><div className="text-sm truncate">{r.name} · {L(r.expedition_title)}</div>
                  <div className="font-mono-hs text-[10px] text-[#737373]">{r.reasons.join(" · ")}</div></div>
                <ChevronRight size={14} className="text-[#737373] shrink-0" />
              </button>
            ))}
        </div>
        <div className="border border-white/10 bg-[#141414] p-6" data-testid="dash-alerts">
          <h3 className="font-clash uppercase tracking-tight mb-4 flex items-center gap-2"><AlertTriangle size={16} className="text-[#8A281E]" /> Alertes ({s.alerts.length})</h3>
          {s.alerts.length === 0 ? <p className="text-[#737373] text-sm">Aucune alerte de solde.</p> :
            s.alerts.map((a) => (
              <div key={a.reservation_id} className="flex justify-between py-2 border-b border-white/5 text-sm">
                <span className="truncate">{a.name} · {L(a.expedition_title)}</span>
                <span className="font-mono-hs text-[#8A281E] text-xs shrink-0">{a.message}</span>
              </div>
            ))}
        </div>
        <div className="border border-white/10 bg-[#141414] p-6">
          <h3 className="font-clash uppercase tracking-tight mb-4">Prochaines expéditions</h3>
          {s.upcoming.map((e) => (
            <div key={e.slug} className="flex justify-between py-2 border-b border-white/5 text-sm">
              <span>{L(e.title)}</span>
              <span className="font-mono-hs text-[#BC5D2E] text-xs">{fmtD(e.next_departure)} · {e.spots_left}/{e.spots_total}</span>
            </div>
          ))}
        </div>
        <div className="border border-white/10 bg-[#141414] p-6">
          <h3 className="font-clash uppercase tracking-tight mb-4">Presque complètes</h3>
          {s.nearly_full.length === 0 ? <p className="text-[#737373] text-sm">Aucune pour le moment.</p> :
            s.nearly_full.map((e) => (
              <div key={e.slug} className="flex justify-between py-2 border-b border-white/5 text-sm">
                <span>{L(e.title)}</span>
                <span className="font-mono-hs text-[#8A281E] text-xs">{e.spots_left}/{e.spots_total}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Expeditions ---------------- */
function Expeditions() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/expeditions").then(({ data }) => {
    data.sort((a, b) => (!!b.best_seller - !!a.best_seller) || ((a.order ?? 999) - (b.order ?? 999)));
    setList(data);
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const del = async (slug) => { if (!window.confirm("Supprimer cette expédition ?")) return; await api.delete(`/admin/expeditions/${slug}`); toast.success("Supprimée"); load(); };
  const toggle = async (e) => { await api.put(`/admin/expeditions/${e.slug}`, { hidden: !e.hidden }); load(); };
  const move = async (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= list.length) return;
    const n = [...list]; const [x] = n.splice(idx, 1); n.splice(to, 0, x);
    setList(n);
    await Promise.all(n.map((e, i) => api.put(`/admin/expeditions/${e.slug}`, { order: i })));
    toast.success("Ordre mis à jour");
  };
  return (
    <div data-testid="admin-expeditions">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-clash text-2xl uppercase tracking-tight">Expéditions</h2>
        <button onClick={() => setEditing({})} className="flex items-center gap-2 bg-[#BC5D2E] text-white px-5 py-3 uppercase tracking-widest text-xs hover:bg-[#8A281E]" data-testid="add-expedition"><Plus size={14} /> Ajouter</button>
      </div>
      <div className="space-y-2">
        {list.map((e, idx) => (
          <div key={e.slug} className="flex items-center gap-3 border border-white/10 bg-[#141414] p-4" data-testid={`admin-exp-${e.slug}`}>
            <div className="flex flex-col">
              <button onClick={() => move(idx, -1)} className="text-[#666] hover:text-white" data-testid={`move-up-${e.slug}`}><ArrowUp size={13} /></button>
              <button onClick={() => move(idx, 1)} className="text-[#666] hover:text-white" data-testid={`move-down-${e.slug}`}><ArrowDown size={13} /></button>
            </div>
            {e.cover && <img src={imgSrc(e.cover)} alt="" className="w-12 h-12 object-cover border border-white/10 shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="font-clash uppercase tracking-tight truncate flex items-center gap-2">{L(e.title)}{e.best_seller && <span className="font-mono-hs text-[9px] uppercase bg-[#BC5D2E] text-white px-1.5 py-0.5 flex items-center gap-1"><Star size={9} />Best</span>}</div>
              <div className="font-mono-hs text-[10px] text-[#737373] uppercase tracking-widest">{L(e.location)} · {e.promo_price ? <span className="text-[#BC5D2E]">{eur(e.promo_price)} (au lieu de {eur(e.price)})</span> : eur(e.price)} · {e.spots_left}/{e.spots_total} pl.</div>
            </div>
            <span className={`font-mono-hs text-[10px] uppercase tracking-widest px-2 py-1 ${e.status === "published" && !e.hidden ? "text-[#BC5D2E] border border-[#BC5D2E]/50" : "text-[#737373] border border-white/10"}`}>{e.hidden ? "masqué" : e.status === "draft" ? "brouillon" : e.status === "published" ? "publié" : e.status}</span>
            <button onClick={() => toggle(e)} className="text-[#A3A3A3] hover:text-white text-xs uppercase tracking-widest">{e.hidden ? "Afficher" : "Masquer"}</button>
            <button onClick={() => setEditing(e)} className="text-[#A3A3A3] hover:text-[#BC5D2E]" data-testid={`edit-${e.slug}`}><Pencil size={16} /></button>
            <button onClick={() => del(e.slug)} className="text-[#A3A3A3] hover:text-[#8A281E]" data-testid={`delete-${e.slug}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing && <ExpeditionForm initial={editing.slug ? editing : null} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

/* ---------------- Reservation Drawer (Fiche) ---------------- */
const PayPill = ({ status }) => {
  const map = { "payé": "text-[#4ade80] border-[#4ade80]/40", "acompte payé": "text-[#BC5D2E] border-[#BC5D2E]/40", "en attente": "text-[#A3A3A3] border-white/20" };
  return <span className={`font-mono-hs text-[10px] uppercase tracking-widest px-2 py-1 border ${map[status] || map["en attente"]}`}>{status}</span>;
};

function ReservationDrawer({ res, onClose, onChanged }) {
  const [r, setR] = useState(res);
  const [mode, setMode] = useState(res.mode_paiement || "Virement");
  const [notes, setNotes] = useState(res.admin_notes || "");
  const [reminder, setReminder] = useState({ channel: "email", text: "" });
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [docMsg, setDocMsg] = useState("");
  const [docBusy, setDocBusy] = useState(false);
  const act = async (payload, successMsg) => {
    try {
      const { data } = await api.post(`/admin/reservations/${r.reservation_id}/action`, payload);
      setR(data.reservation); onChanged();
      toast.success(successMsg || "Mis à jour ✓");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const accept = async (payment_mode) => {
    setAcceptOpen(false);
    await act({ action: "accept", payment_mode });
  };
  const requestDocs = async () => {
    if (!docFile) { toast.error("Sélectionnez un fichier PDF."); return; }
    setDocBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", docFile);
      fd.append("message", docMsg);
      const { data } = await api.post(`/admin/reservations/${r.reservation_id}/request-documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setR(data.reservation); onChanged();
      toast[data.sent ? "success" : "warning"](data.sent ? "Documents envoyés au client ✓" : "Enregistré, mais email non envoyé (vérifier Resend)");
      setDocOpen(false); setDocFile(null); setDocMsg("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setDocBusy(false); }
  };
  const del = async () => { if (!window.confirm("Supprimer cette réservation ?")) return; await api.delete(`/admin/reservations/${r.reservation_id}`); toast.success("Supprimée"); onChanged(); onClose(); };
  const tl = [...(r.timeline || [])].reverse();
  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-xl bg-[#101010] border-l border-white/15 h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="reservation-drawer">
        <div className="sticky top-0 bg-[#101010] border-b border-white/10 p-5 flex items-center justify-between z-10">
          <div>
            <div className="flex items-center gap-2"><h3 className="font-clash text-xl uppercase tracking-tight">{r.name}</h3>{r.is_test && <TestBadge />}</div>
            <div className="font-mono-hs text-[10px] text-[#737373] uppercase tracking-widest">{r.reservation_id}</div>
          </div>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white" data-testid="drawer-close"><X /></button>
        </div>
        <div className="p-5 space-y-6">
          {/* Client */}
          <section>
            <h4 className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] mb-2">Client</h4>
            <div className="text-sm space-y-1 text-[#D4D4D4]">
              <div>{r.email}</div>
              <div className="flex items-center gap-2">{r.phone || "—"}{r.is_whatsapp && <span className="font-mono-hs text-[9px] uppercase tracking-widest bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/30 px-1.5 py-0.5">WhatsApp</span>}{r.country && <span className="font-mono-hs text-[10px] text-[#737373]">· {r.country}</span>}</div>
              <div>{r.participants} participant(s) · départ {fmtD(r.departure_date || r.date)}</div>
            </div>
          </section>
          {/* Expedition */}
          <section>
            <h4 className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] mb-2">Expédition</h4>
            <div className="text-sm text-[#D4D4D4]">{L(r.expedition_title)} <span className="font-mono-hs text-[10px] text-[#BC5D2E] uppercase">· {r.environment}</span></div>
          </section>
          {/* Candidature */}
          <section className="border border-white/10 p-4" data-testid="drawer-candidature">
            <h4 className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] mb-3">Traiter la candidature</h4>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setAcceptOpen(true)} className="py-2 text-xs uppercase tracking-widest bg-[#BC5D2E] text-white hover:bg-[#8A281E]" data-testid="cand-accept">Accepter (Paiement)</button>
              <button onClick={() => { const m = window.prompt("Message au client (infos demandées) :", ""); if (m !== null) act({ action: "request_info", message: m }); }} className="py-2 text-xs uppercase tracking-widest border border-white/20 hover:border-white" data-testid="cand-info">Demander infos</button>
              <button onClick={() => setDocOpen(true)} className="py-2 text-xs uppercase tracking-widest border border-white/20 hover:border-white" data-testid="cand-docs">Demander des documents</button>
              <button onClick={() => { if (window.confirm("Refuser cette candidature ?")) act({ action: "refuse" }); }} className="py-2 text-xs uppercase tracking-widest border border-[#8A281E]/50 text-[#8A281E] hover:bg-[#8A281E]/10" data-testid="cand-refuse">Refuser</button>
            </div>
            {r.pay_token && (
              <div className="mt-3 flex items-center gap-2">
                <input readOnly value={`${window.location.origin}/payer/${r.pay_token}`} className="flex-1 bg-[#0F0F0F] border border-white/15 px-2 py-1 text-[10px] text-[#A3A3A3]" data-testid="drawer-paylink" />
                <button onClick={async () => { const link = `${window.location.origin}/payer/${r.pay_token}`; try { await navigator.clipboard.writeText(link); } catch { const ta = document.createElement("textarea"); ta.value = link; document.body.appendChild(ta); ta.select(); try { document.execCommand("copy"); } catch {} document.body.removeChild(ta); } toast.success("Lien copié"); }} className="border border-white/20 px-2 py-1 text-[10px] uppercase hover:border-white">Copier</button>
              </div>
            )}
            {r.option_expires_at && !r.acompte_paye && r.status === "Acceptée" && (
              <p className="mt-2 font-mono-hs text-[10px] text-[#BC5D2E]">Option valable jusqu'au {fmtDT(r.option_expires_at)} (72h).</p>
            )}
          </section>
          {/* Statut */}
          <section>
            <h4 className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] mb-2">Statut</h4>
            <select value={r.status} onChange={(e) => act({ action: "set_status", status: e.target.value })} className={inp} data-testid="drawer-status">
              {RES_STATUS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </section>
          {/* Paiement */}
          <section className="border border-white/10 p-4" data-testid="drawer-payment">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] flex items-center gap-2"><Wallet size={13} /> Suivi paiement</h4>
              <PayPill status={r.payment_status} />
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div>Total <span className="font-clash text-[#F5F5F0]">{eur(r.amount_total)}</span></div>
              <div>Acompte 30 % <span className="font-clash text-[#BC5D2E]" data-testid="drawer-deposit">{eur(r.deposit_due)}</span></div>
              <div>Encaissé <span className="font-clash text-[#4ade80]" data-testid="drawer-encaisse">{eur(r.encaisse)}</span></div>
              <div>Reste <span className="font-clash text-[#F5F5F0]">{eur(r.reste_a_encaisser)}</span></div>
              {r.balance_deadline && <div className="col-span-2 font-mono-hs text-[10px] text-[#737373]">Échéance solde : {r.balance_deadline}</div>}
            </div>
            {(r.payment_history || []).length > 0 && (
              <div className="mb-3 border-t border-white/5 pt-2" data-testid="drawer-payhistory">
                {r.payment_history.map((h, i) => (
                  <div key={i} className="flex justify-between text-[11px] text-[#A3A3A3] py-0.5"><span>{fmtDT(h.at)} · {h.type} · Stripe</span><span>{eur(h.amount)}</span></div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 mb-2">
              <select value={mode} onChange={(e) => setMode(e.target.value)} className="bg-[#0F0F0F] border border-white/15 px-2 py-1 text-xs">{PAY_MODES.map((m) => <option key={m}>{m}</option>)}</select>
              <button onClick={() => act({ action: "mark_deposit_paid", value: !r.acompte_paye, mode })} className={`flex-1 py-2 text-xs uppercase tracking-widest border ${r.acompte_paye ? "border-[#4ade80]/40 text-[#4ade80]" : "border-[#BC5D2E] bg-[#BC5D2E] text-white"}`} data-testid="drawer-deposit-btn">
                {r.acompte_paye ? "Acompte payé ✓" : "Marquer acompte payé"}
              </button>
            </div>
            <button onClick={() => act({ action: "mark_balance_paid", value: !r.solde_paye })} className={`w-full py-2 text-xs uppercase tracking-widest border ${r.solde_paye ? "border-[#4ade80]/40 text-[#4ade80]" : "border-white/20 text-[#D4D4D4] hover:border-white"}`} data-testid="drawer-balance-btn">
              {r.solde_paye ? "Solde payé ✓ (complet)" : "Marquer solde payé"}
            </button>
          </section>
          {/* Documents */}
          <section data-testid="drawer-documents">
            <h4 className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] mb-2">Documents ({(r.documents || []).filter((d) => d.received).length}/{(r.documents || []).length})</h4>
            <div className="space-y-1">
              {(r.documents || []).map((d) => (
                <div key={d.key} className="flex items-center gap-2 text-xs border-b border-white/5 py-1.5" data-testid={`doc-${d.key}`}>
                  <span className={`flex-1 ${d.received ? "text-[#D4D4D4]" : "text-[#737373]"}`}>{d.label}{d.required && <span className="text-[#8A281E]">*</span>}</span>
                  <button onClick={() => act({ action: "doc", key: d.key, field: "received", value: !d.received })} className={`px-2 py-0.5 border text-[10px] uppercase ${d.received ? "border-[#BC5D2E] text-[#BC5D2E]" : "border-white/15 text-[#737373]"}`} data-testid={`doc-recv-${d.key}`}>{d.received ? "Reçu" : "Reçu ?"}</button>
                  <button onClick={() => act({ action: "doc", key: d.key, field: "validated", value: !d.validated })} className={`px-2 py-0.5 border text-[10px] uppercase ${d.validated ? "border-[#4ade80] text-[#4ade80]" : "border-white/15 text-[#737373]"}`}>{d.validated ? "Validé" : "Valider"}</button>
                </div>
              ))}
            </div>
          </section>
          {/* Relance */}
          <section>
            <h4 className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] mb-2">Relance</h4>
            <div className="flex gap-2">
              <select value={reminder.channel} onChange={(e) => setReminder({ ...reminder, channel: e.target.value })} className="bg-[#0F0F0F] border border-white/15 px-2 py-1 text-xs"><option value="email">Email</option><option value="téléphone">Téléphone</option><option value="whatsapp">WhatsApp</option></select>
              <input value={reminder.text} onChange={(e) => setReminder({ ...reminder, text: e.target.value })} placeholder="Objet de la relance" className="flex-1 bg-[#0F0F0F] border border-white/15 px-2 py-1 text-xs" data-testid="reminder-input" />
              <button onClick={() => { if (!reminder.text) return; act({ action: "add_reminder", ...reminder }); setReminder({ ...reminder, text: "" }); }} className="bg-[#BC5D2E] text-white px-3 text-xs uppercase" data-testid="reminder-btn">OK</button>
            </div>
            <button onClick={() => { if (window.confirm("Envoyer l'email de rappel de solde au client ?")) act({ action: "send_balance_reminder" }, "Rappel de solde envoyé ✓"); }} className="mt-2 w-full flex items-center justify-center gap-2 border border-[#BC5D2E]/50 text-[#BC5D2E] py-2 text-xs uppercase tracking-widest hover:bg-[#BC5D2E]/10" data-testid="send-balance-reminder-btn"><Send size={13} /> Envoyer le rappel de solde</button>
          </section>
          {/* Notes */}
          <section>
            <h4 className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] mb-2">Notes internes</h4>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inp} data-testid="drawer-notes" />
            <button onClick={() => act({ action: "add_note", notes })} className="mt-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-white">Enregistrer la note</button>
          </section>
          {/* Timeline */}
          <section data-testid="drawer-timeline">
            <h4 className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] mb-2 flex items-center gap-2"><Clock size={13} /> Timeline</h4>
            <div className="space-y-2">
              {tl.map((t, i) => (
                <div key={i} className="flex gap-3 text-xs">
                  <span className="font-mono-hs text-[#737373] shrink-0 w-24">{fmtDT(t.at)}</span>
                  <span className="text-[#D4D4D4]"><span className="text-[#BC5D2E]">{t.action}</span>{t.detail ? ` — ${t.detail}` : ""}</span>
                </div>
              ))}
            </div>
          </section>
          <div className="flex gap-2 pt-2 border-t border-white/10">
            <button onClick={() => act({ action: "mark_test", value: !r.is_test })} className="flex-1 border border-white/20 py-2 text-xs uppercase tracking-widest hover:border-white flex items-center justify-center gap-2"><FlaskConical size={13} /> {r.is_test ? "Retirer TEST" : "Marquer TEST"}</button>
            <button onClick={del} className="flex-1 border border-[#8A281E]/50 text-[#8A281E] py-2 text-xs uppercase tracking-widest hover:bg-[#8A281E]/10" data-testid="drawer-delete">Supprimer</button>
          </div>
        </div>

        {acceptOpen && (
          <div className="fixed inset-0 z-[95] bg-black/70 flex items-center justify-center p-4" onClick={() => setAcceptOpen(false)} data-testid="accept-modal">
            <div className="w-full max-w-md bg-[#141414] border border-white/15 p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-clash text-lg uppercase tracking-tight">Accepter — quel lien envoyer ?</h3>
                <button onClick={() => setAcceptOpen(false)} className="text-[#A3A3A3] hover:text-white" data-testid="accept-modal-close"><X size={18} /></button>
              </div>
              <p className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] mb-4">Total {eur(r.amount_total)} · un email Resend avec le lien de paiement (72h) sera envoyé automatiquement au client.</p>
              <div className="space-y-3">
                <button onClick={() => accept("deposit")} className="w-full py-3 text-xs uppercase tracking-widest bg-[#BC5D2E] text-white hover:bg-[#8A281E] flex items-center justify-between px-4" data-testid="accept-deposit">
                  <span>Lien acompte (30 %)</span><span className="font-clash">{eur(Math.round((r.amount_total || 0) * 0.30))}</span>
                </button>
                <button onClick={() => accept("full")} className="w-full py-3 text-xs uppercase tracking-widest border border-white/25 text-[#F5F5F0] hover:border-white flex items-center justify-between px-4" data-testid="accept-full">
                  <span>Lien paiement intégral (100 %)</span><span className="font-clash">{eur(r.amount_total)}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {docOpen && (
          <div className="fixed inset-0 z-[95] bg-black/70 flex items-center justify-center p-4" onClick={() => setDocOpen(false)} data-testid="docs-modal">
            <div className="w-full max-w-md bg-[#141414] border border-white/15 p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-clash text-lg uppercase tracking-tight">Demander des documents</h3>
                <button onClick={() => setDocOpen(false)} className="text-[#A3A3A3] hover:text-white" data-testid="docs-modal-close"><X size={18} /></button>
              </div>
              <p className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] mb-4">Sélectionnez un PDF (documents obligatoires + fiche d'expédition). Il sera envoyé automatiquement au client par email.</p>
              <input type="file" accept="application/pdf,.pdf" onChange={(e) => setDocFile(e.target.files?.[0] || null)} className="w-full text-xs text-[#D4D4D4] file:mr-3 file:border file:border-white/20 file:bg-transparent file:text-[#F5F5F0] file:px-3 file:py-2 file:text-xs file:uppercase file:tracking-widest" data-testid="docs-file" />
              {docFile && <p className="mt-2 font-mono-hs text-[10px] text-[#BC5D2E] truncate">{docFile.name}</p>}
              <textarea rows={3} value={docMsg} onChange={(e) => setDocMsg(e.target.value)} placeholder="Message optionnel au client…" className={`${inp} mt-3`} data-testid="docs-message" />
              <button onClick={requestDocs} disabled={docBusy || !docFile} className="mt-4 w-full py-3 text-xs uppercase tracking-widest bg-[#BC5D2E] text-white hover:bg-[#8A281E] disabled:opacity-40" data-testid="docs-send">{docBusy ? "Envoi…" : "Envoyer au client"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Reservations (grouped) ---------------- */
function Reservations() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const [showTest, setShowTest] = useState(true);
  const load = useCallback(() => api.get("/admin/reservations").then(({ data }) => setList(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const seed = async () => { await api.post("/admin/reservations/seed-test"); toast.success("Données de test créées"); load(); };
  const purge = async () => { if (!window.confirm("Supprimer toutes les données de test ?")) return; const { data } = await api.post("/admin/reservations/purge-test"); toast.success(`${data.deleted} supprimée(s)`); load(); };

  const filtered = showTest ? list : list.filter((r) => !r.is_test);
  const groups = {};
  filtered.forEach((r) => { const k = L(r.expedition_title) || "Autre"; (groups[k] = groups[k] || []).push(r); });

  return (
    <div data-testid="admin-reservations">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h2 className="font-clash text-2xl uppercase tracking-tight">Réservations</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1 text-xs text-[#A3A3A3]"><input type="checkbox" checked={showTest} onChange={(e) => setShowTest(e.target.checked)} className="accent-[#BC5D2E]" data-testid="toggle-test" /> Afficher TEST</label>
          <button onClick={seed} className="border border-white/20 px-3 py-2 text-xs uppercase tracking-widest hover:border-white" data-testid="seed-test">+ Données test</button>
          <button onClick={purge} className="border border-[#8A281E]/50 text-[#8A281E] px-3 py-2 text-xs uppercase tracking-widest hover:bg-[#8A281E]/10" data-testid="purge-test">Purger TEST</button>
        </div>
      </div>
      {filtered.length === 0 && <p className="text-[#737373] text-sm">Aucune réservation.</p>}
      <div className="space-y-6">
        {Object.entries(groups).map(([title, rows]) => (
          <div key={title}>
            <div className="flex items-center justify-between border-b border-[#BC5D2E]/30 pb-2 mb-2">
              <h3 className="font-clash uppercase tracking-tight text-[#BC5D2E]">{title}</h3>
              <span className="font-mono-hs text-[10px] text-[#737373] uppercase tracking-widest">{rows.length} résa · {eur(rows.reduce((a, r) => a + (r.encaisse || 0), 0))} encaissé</span>
            </div>
            <div className="space-y-2">
              {rows.map((r) => (
                <button key={r.reservation_id} onClick={() => setOpen(r)} className="w-full text-left flex flex-wrap items-center gap-x-4 gap-y-1 border border-white/10 bg-[#141414] p-3 hover:border-[#BC5D2E]/50" data-testid={`res-${r.reservation_id}`}>
                  <span className="font-clash uppercase tracking-tight">{r.name}</span>
                  {r.is_test && <TestBadge />}
                  <span className="text-[#A3A3A3] text-sm">{r.email}</span>
                  <span className="font-mono-hs text-[10px] text-[#737373]">{r.participants} pers · {eur(r.amount_total)}</span>
                  <span className="ml-auto flex items-center gap-2">
                    {r.docs_pending > 0 && <span className="font-mono-hs text-[10px] text-[#8A281E]">{r.docs_pending} doc</span>}
                    <PayPill status={r.payment_status} />
                    <span className="font-mono-hs text-[10px] uppercase text-[#A3A3A3] border border-white/10 px-2 py-0.5">{r.status}</span>
                    <ChevronRight size={14} className="text-[#737373]" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {open && <ReservationDrawer res={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}

/* ---------------- Clients CRM ---------------- */
function Clients() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/clients").then(({ data }) => setList(data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-clients">
      <h2 className="font-clash text-2xl uppercase tracking-tight mb-6">Clients</h2>
      {list.length === 0 && <p className="text-[#737373] text-sm">Aucun client pour l'instant.</p>}
      <div className="space-y-3">
        {list.map((c) => (
          <div key={c.email} className="border border-white/10 bg-[#141414] p-4" data-testid={`client-${c.email}`}>
            <div className="flex justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2"><span className="font-clash uppercase tracking-tight">{c.name}</span>{c.is_test && <TestBadge />}<span className="text-[#A3A3A3] text-sm">· {c.email} · {c.phone || "—"}</span></div>
              <div className="font-mono-hs text-sm"><span className="text-[#4ade80]">{eur(c.encaisse)}</span> <span className="text-[#737373]">/ {eur(c.total)}</span></div>
            </div>
            <div className="mt-2 text-xs text-[#737373]">{c.reservations.length} réservation(s) : {c.reservations.map((r) => `${L(r.expedition_title)} (${r.status})`).join(", ")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Contacts & Leads ---------------- */
const CONTACT_STATUS = ["Nouveau", "À traiter", "Contacté", "Converti", "Archivé"];
const STATUS_DOT = { "Nouveau": "bg-[#BC5D2E]", "À traiter": "bg-yellow-500", "Contacté": "bg-[#4ade80]", "Converti": "bg-[#38bdf8]", "Archivé": "bg-[#737373]" };

function ContactCard({ c, onChange, selected, onSelect }) {
  const [notes, setNotes] = useState(c.admin_notes || "");
  const upd = async (patch) => { await api.put(`/admin/contacts/${c.id}`, patch); toast.success("Mis à jour ✓"); onChange(); };
  const del = async () => { if (!window.confirm("Supprimer cette demande ?")) return; await api.delete(`/admin/contacts/${c.id}`); toast.success("Supprimée"); onChange(); };
  const st = CONTACT_STATUS.includes(c.status) ? c.status : "Nouveau";
  return (
    <div className={`border ${selected ? "border-[#BC5D2E]" : "border-white/10"} bg-[#141414] p-4`} data-testid={`contact-${c.id}`}>
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={selected} onChange={onSelect} className="accent-[#BC5D2E]" data-testid={`contact-select-${c.id}`} />
          <span className={`w-2 h-2 rounded-full ${STATUS_DOT[st]}`} /><span className="font-clash uppercase tracking-tight">{c.name}</span><span className="font-mono-hs text-[10px] text-[#BC5D2E] uppercase">{c.type}</span>
        </div>
        <div className="flex items-center gap-2">
          <select value={st} onChange={(e) => upd({ status: e.target.value })} className={`bg-[#0F0F0F] border px-2 py-1 text-xs ${st === "Contacté" ? "border-[#4ade80]/50 text-[#4ade80]" : "border-white/15"}`} data-testid={`contact-status-${c.id}`}>{CONTACT_STATUS.map((s) => <option key={s}>{s}</option>)}</select>
          <button onClick={del} className="text-[#A3A3A3] hover:text-[#8A281E]" data-testid={`contact-delete-${c.id}`}><Trash2 size={15} /></button>
        </div>
      </div>
      <div className="text-[#A3A3A3] text-xs mt-1">{c.email} · {fmtD(c.created_at)}</div>
      <p className="text-[#D4D4D4] text-sm mt-2 whitespace-pre-line">{c.message}</p>
      <div className="mt-3 flex gap-2">
        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note interne" className="flex-1 bg-[#0F0F0F] border border-white/15 px-2 py-1 text-xs" data-testid={`contact-notes-${c.id}`} />
        <button onClick={() => upd({ admin_notes: notes })} className="border border-white/20 px-3 text-xs uppercase tracking-widest hover:border-white">OK</button>
      </div>
    </div>
  );
}

function Contacts() {
  const [list, setList] = useState([]);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("contacts");
  const [selC, setSelC] = useState([]);
  const [selL, setSelL] = useState([]);
  const loadContacts = useCallback(() => api.get("/admin/contacts").then(({ data }) => setList(data)).catch(() => {}), []);
  const loadLeads = useCallback(() => api.get("/admin/leads").then(({ data }) => setLeads(data)).catch(() => {}), []);
  useEffect(() => { loadContacts(); loadLeads(); }, [loadContacts, loadLeads]);

  const toggle = (arr, setArr, id) => setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  const bulkC = async (action, status) => {
    if (!selC.length) return;
    if (action === "delete" && !window.confirm(`Supprimer ${selC.length} demande(s) ?`)) return;
    await api.post("/admin/contacts/bulk", { ids: selC, action, status });
    toast.success("Action appliquée"); setSelC([]); loadContacts();
  };
  const bulkL = async (action, status) => {
    if (!selL.length) return;
    if (action === "delete" && !window.confirm(`Supprimer ${selL.length} lead(s) ?`)) return;
    await api.post("/admin/leads/bulk", { emails: selL, action, status });
    toast.success("Action appliquée"); setSelL([]); loadLeads();
  };
  const updLead = async (email, status) => { await api.put("/admin/leads", { email, status }); toast.success("Mis à jour"); loadLeads(); };

  const BulkBar = ({ count, onStatus, onDelete }) => count > 0 && (
    <div className="flex items-center gap-3 flex-wrap bg-[#1a1a1a] border border-[#BC5D2E]/30 p-3 mb-4" data-testid="bulk-bar">
      <span className="font-mono-hs text-xs text-[#BC5D2E] uppercase">{count} sélectionné(s)</span>
      <select onChange={(e) => { if (e.target.value) { onStatus(e.target.value); e.target.value = ""; } }} className="bg-[#0F0F0F] border border-white/15 px-2 py-1 text-xs" data-testid="bulk-status">
        <option value="">Changer le statut…</option>{CONTACT_STATUS.map((s) => <option key={s}>{s}</option>)}
      </select>
      <button onClick={onDelete} className="border border-[#8A281E]/50 text-[#8A281E] px-3 py-1 text-xs uppercase tracking-widest hover:bg-[#8A281E]/10" data-testid="bulk-delete">Supprimer</button>
    </div>
  );

  return (
    <div data-testid="admin-contacts">
      <h2 className="font-clash text-2xl uppercase tracking-tight mb-6">Contacts & Leads</h2>
      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("contacts")} className={`px-4 py-2 text-xs uppercase tracking-widest border ${tab === "contacts" ? "bg-[#BC5D2E] border-[#BC5D2E] text-white" : "border-white/20 text-[#A3A3A3]"}`} data-testid="tab-contacts">Candidatures ({list.length})</button>
        <button onClick={() => setTab("leads")} className={`px-4 py-2 text-xs uppercase tracking-widest border ${tab === "leads" ? "bg-[#BC5D2E] border-[#BC5D2E] text-white" : "border-white/20 text-[#A3A3A3]"}`} data-testid="tab-leads">Leads accès ({leads.length})</button>
      </div>
      {tab === "contacts" ? (
        <>
          <BulkBar count={selC.length} onStatus={(s) => bulkC("status", s)} onDelete={() => bulkC("delete")} />
          <div className="space-y-3">
            {list.length === 0 && <p className="text-[#737373] text-sm">Aucune demande.</p>}
            {list.map((c) => <ContactCard key={c.id} c={c} onChange={loadContacts} selected={selC.includes(c.id)} onSelect={() => toggle(selC, setSelC, c.id)} />)}
          </div>
        </>
      ) : (
        <>
          <BulkBar count={selL.length} onStatus={(s) => bulkL("status", s)} onDelete={() => bulkL("delete")} />
          <div className="space-y-2">
            {leads.length === 0 && <p className="text-[#737373] text-sm">Aucun lead.</p>}
            {leads.map((l) => {
              const st = CONTACT_STATUS.includes(l.status) ? l.status : (l.status || "Nouveau");
              return (
                <div key={l.email} className="flex items-center gap-3 border border-white/10 bg-[#141414] p-3 text-sm flex-wrap" data-testid={`lead-${l.email}`}>
                  <input type="checkbox" checked={selL.includes(l.email)} onChange={() => toggle(selL, setSelL, l.email)} className="accent-[#BC5D2E]" data-testid={`lead-select-${l.email}`} />
                  <span className="flex-1 min-w-0 truncate">{l.first_name ? `${l.first_name} · ` : ""}{l.email}{l.promo_code && <span className="font-mono-hs text-[10px] text-[#BC5D2E] ml-2">{l.promo_code}</span>}</span>
                  <span className="font-mono-hs text-[10px] text-[#737373]">{fmtD(l.created_at)}</span>
                  <select value={CONTACT_STATUS.includes(st) ? st : "Nouveau"} onChange={(e) => updLead(l.email, e.target.value)} className="bg-[#0F0F0F] border border-white/15 px-2 py-1 text-xs" data-testid={`lead-status-${l.email}`}>
                    {[...new Set([st, ...CONTACT_STATUS, "Code promotionnel envoyé"])].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Pages CMS ---------------- */
const FIELD_LABELS = {
  hero_kicker: "Accroche héro", hero_title: "Titre héro", hero_sub: "Sous-titre héro", cta: "Bouton (CTA)",
  seo_title: "SEO — Titre (meta / og:title)", seo_description: "SEO — Description (meta / og:description)",
  title: "Titre de la page", kicker: "Accroche", lead: "Phrase d'accroche", p1: "Paragraphe 1", p2: "Paragraphe 2",
  p3: "Paragraphe 3", quote: "Citation", email: "Email de contact", success_title: "Titre confirmation",
  success_text: "Texte confirmation", expeditions: "Menu — Expéditions", story: "Menu — Histoire",
  contact: "Menu — Contact", cta_book: "Menu — Bouton réserver", tagline: "Slogan du footer",
  instagram_url: "Lien Instagram", instagram_label: "Libellé Instagram", col_expeditions: "Colonne — Expéditions",
  col_story: "Colonne — Histoire", col_contact: "Colonne — Contact", legal_mentions: "Lien — Mentions légales",
  legal_cgv: "Lien — CGV", legal_privacy: "Lien — Confidentialité", legal_cookies: "Lien — Cookies",
  cookie_settings: "Lien — Réglages cookies", copyright: "Copyright",
  operator: "Opérateur / raison sociale", legal_form: "Forme juridique", siren: "SIREN", siret: "SIRET",
  tva: "N° TVA intracommunautaire", address: "Adresse du siège", phone: "Téléphone", director: "Directeur de publication",
  atout_france: "N° Atout France", guarantee: "Garantie financière", rc_pro: "Assurance RC Pro",
  host: "Hébergeur", host_address: "Adresse hébergeur", ip_text: "Propriété intellectuelle", law_text: "Droit applicable",
  body: "Corps du texte",
};
const PAGE_SCHEMA = {
  home: [["hero_kicker", "text"], ["hero_title", "text"], ["hero_sub", "area"], ["cta", "text"], ["seo_title", "text"], ["seo_description", "area"]],
  about: [["seo_title", "text"], ["seo_description", "area"], ["kicker", "text"], ["lead", "area"], ["p1", "big"], ["p2", "big"], ["p3", "big"], ["quote", "area"], ["cta", "text"]],
  contact: [["seo_title", "text"], ["seo_description", "area"], ["kicker", "text"], ["title", "text"], ["email", "text"], ["success_title", "text"], ["success_text", "area"]],
  nav: [["expeditions", "text"], ["story", "text"], ["contact", "text"], ["cta_book", "text"]],
  footer: [["tagline", "area"], ["email", "text"], ["instagram_url", "text"], ["instagram_label", "text"], ["col_expeditions", "text"], ["col_story", "text"], ["col_contact", "text"], ["legal_mentions", "text"], ["legal_cgv", "text"], ["legal_privacy", "text"], ["legal_cookies", "text"], ["cookie_settings", "text"], ["copyright", "text"]],
  legal_mentions: [["title", "text"], ["seo_description", "area"], ["operator", "text"], ["legal_form", "text"], ["siren", "text"], ["siret", "text"], ["tva", "text"], ["address", "area"], ["email", "text"], ["phone", "text"], ["director", "text"], ["atout_france", "text"], ["guarantee", "text"], ["rc_pro", "text"], ["host", "text"], ["host_address", "area"], ["ip_text", "area"], ["law_text", "area"]],
  legal_cgv: [["title", "text"], ["seo_description", "area"], ["body", "big"]],
  legal_confidentialite: [["title", "text"], ["seo_description", "area"], ["body", "big"]],
  legal_cookies: [["title", "text"], ["seo_description", "area"], ["body", "big"]],
};
const schemaFor = (key) => PAGE_SCHEMA[key] || [["title", "text"], ["seo_description", "area"], ["body", "big"]];
const TITLE_PRESETS = [["0.9", "Petit"], ["1", "Normal"], ["1.1", "Grand"], ["1.25", "Très grand"]];
const BODY_PRESETS = [["0.9", "Compact"], ["1", "Normal"], ["1.1", "Confortable"], ["1.2", "Large"]];

function Pages() {
  const [pages, setPages] = useState([]);
  const [active, setActive] = useState(null);
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({ title_scale: 1, body_scale: 1 });
  const [creating, setCreating] = useState(null); // {name, slug}

  const loadList = useCallback(() => api.get("/admin/pages").then(({ data }) => setPages(data.pages)).catch(() => {}), []);
  useEffect(() => { loadList(); api.get("/site-settings").then(({ data }) => setSettings({ title_scale: data.title_scale || 1, body_scale: data.body_scale || 1 })).catch(() => {}); }, [loadList]);

  const openEdit = (key) => { setActive(key); api.get(`/admin/pages/${key}`).then(({ data }) => setContent(data.content || {})).catch(() => setContent({})); };
  const save = async () => {
    setSaving(true);
    try { await api.put(`/admin/pages/${active}`, { content }); toast.success("Page enregistrée ✓"); loadList(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };
  const del = async (p) => {
    const isSystem = p.kind !== "custom";
    if (!window.confirm(isSystem ? "Réinitialiser cette page au texte par défaut du site ?" : `Supprimer définitivement la page « ${p.name} » ?`)) return;
    try { await api.delete(`/admin/pages/${p.key}`); toast.success(isSystem ? "Réinitialisée ✓" : "Page supprimée ✓"); loadList(); if (active === p.key) setActive(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const createPage = async () => {
    if (!creating?.name || !creating?.slug) { toast.error("Nom et slug requis."); return; }
    try { const { data } = await api.post("/admin/pages", creating); toast.success("Page créée ✓"); setCreating(null); loadList(); openEdit(data.key); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const saveSettings = async (next) => {
    setSettings(next);
    document.documentElement.style.setProperty("--hs-title-scale", next.title_scale);
    document.documentElement.style.setProperty("--hs-body-scale", next.body_scale);
    try { await api.put("/admin/site-settings", next); toast.success("Typographie mise à jour ✓"); } catch { toast.error("Erreur"); }
  };
  const [migrating, setMigrating] = useState(false);
  const migrateImages = async () => {
    setMigrating(true);
    try {
      const { data: dry } = await api.post("/admin/migrate-images-to-cloudinary?dry_run=true");
      if (!dry.images_found) { toast.success("Aucune image locale à migrer ✓"); setMigrating(false); return; }
      if (!window.confirm(`${dry.images_found} image(s) locale(s) détectée(s). Les migrer vers Cloudinary et mettre à jour les liens ?`)) { setMigrating(false); return; }
      const { data } = await api.post("/admin/migrate-images-to-cloudinary");
      toast.success(`${data.images_migrated} image(s) migrée(s), ${data.docs_updated} doc(s) mis à jour${data.errors.length ? ` — ${data.errors.length} erreur(s)` : ""} ✓`);
      loadList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de migration"); }
    finally { setMigrating(false); }
  };

  const kindBadge = (k) => k === "custom" ? "Personnalisée" : k === "legal" ? "Légale" : "Système";

  if (active) {
    const cur = pages.find((p) => p.key === active);
    return (
      <div data-testid="admin-pages">
        <button onClick={() => setActive(null)} className="text-[#A3A3A3] hover:text-white text-xs uppercase tracking-widest mb-4" data-testid="page-back">← Retour aux pages</button>
        <h2 className="font-clash text-2xl uppercase tracking-tight mb-1">{cur?.name || active}</h2>
        <p className="text-[#737373] text-sm mb-6">Laissez un champ vide pour conserver le texte par défaut du site.</p>
        <div className="space-y-4 max-w-2xl">
          {schemaFor(active).map(([fld, type]) => (
            <Field key={fld} label={FIELD_LABELS[fld] || fld}>
              {type === "text"
                ? <input className={inp} value={content[fld] || ""} onChange={(e) => setContent({ ...content, [fld]: e.target.value })} data-testid={`page-${fld}`} />
                : <textarea rows={type === "big" ? 10 : 3} className={`${inp} ${type === "big" ? "font-mono-hs text-xs leading-relaxed" : ""}`} value={content[fld] || ""} onChange={(e) => setContent({ ...content, [fld]: e.target.value })} data-testid={`page-${fld}`} />}
            </Field>
          ))}
          {schemaFor(active).some(([, t]) => t === "big") && <p className="font-mono-hs text-[10px] text-[#737373]">Astuce : une ligne commençant par « ## » devient un sous-titre. Séparez les paragraphes par une ligne vide.</p>}
          <button onClick={save} disabled={saving} className="bg-[#BC5D2E] text-white px-6 py-3 uppercase tracking-widest text-xs hover:bg-[#8A281E] disabled:opacity-50" data-testid="page-save">{saving ? "Enregistrement…" : "Enregistrer"}</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-pages">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-clash text-2xl uppercase tracking-tight">Pages du site</h2>
          <p className="text-[#737373] text-sm mt-1">Gérez le contenu, le SEO et la typographie du site.</p>
        </div>
        <button onClick={() => setCreating({ name: "", slug: "" })} className="flex items-center gap-2 bg-[#BC5D2E] text-white px-4 py-2 uppercase tracking-widest text-xs hover:bg-[#8A281E]" data-testid="page-new-btn"><Plus size={14} /> Nouvelle page</button>
      </div>

      {/* Typographie globale */}
      <div className="border border-white/10 bg-[#141414] p-4 mb-8" data-testid="typography-settings">
        <div className="font-mono-hs text-[10px] uppercase tracking-widest text-[#BC5D2E] mb-3">Typographie globale du site</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <span className="text-xs text-[#A3A3A3] uppercase tracking-widest">Taille des titres</span>
            <div className="flex gap-1.5 mt-2">
              {TITLE_PRESETS.map(([v, l]) => (
                <button key={v} onClick={() => saveSettings({ ...settings, title_scale: Number(v) })} data-testid={`title-scale-${v}`} className={`px-3 py-1.5 text-xs border ${String(settings.title_scale) === v ? "bg-[#BC5D2E] border-[#BC5D2E] text-white" : "border-white/20 text-[#A3A3A3]"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <span className="text-xs text-[#A3A3A3] uppercase tracking-widest">Taille du texte</span>
            <div className="flex gap-1.5 mt-2">
              {BODY_PRESETS.map(([v, l]) => (
                <button key={v} onClick={() => saveSettings({ ...settings, body_scale: Number(v) })} data-testid={`body-scale-${v}`} className={`px-3 py-1.5 text-xs border ${String(settings.body_scale) === v ? "bg-[#BC5D2E] border-[#BC5D2E] text-white" : "border-white/20 text-[#A3A3A3]"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2" data-testid="pages-list">
        {pages.map((p) => (
          <div key={p.key} className="border border-white/10 bg-[#141414] p-4 hover:border-[#BC5D2E]/40 transition-colors" data-testid={`page-card-${p.key}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-[#BC5D2E] shrink-0" />
                  <span className="font-clash uppercase tracking-tight truncate">{p.name}</span>
                </div>
                <span className="font-mono-hs text-[9px] uppercase tracking-widest text-[#737373]">{kindBadge(p.kind)}{p.slug ? ` · /p/${p.slug}` : ""}</span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openEdit(p.key)} className="p-2 border border-white/15 text-[#A3A3A3] hover:text-white hover:border-white" data-testid={`page-edit-${p.key}`}><Pencil size={13} /></button>
                <button onClick={() => del(p)} className="p-2 border border-white/15 text-[#A3A3A3] hover:text-[#8A281E] hover:border-[#8A281E]" data-testid={`page-delete-${p.key}`}><Trash2 size={13} /></button>
              </div>
            </div>
            <p className="text-sm text-[#A3A3A3] mt-3 line-clamp-2">{p.preview}</p>
            {p.updated_at && <div className="font-mono-hs text-[9px] uppercase tracking-widest text-[#737373] mt-2">Modifié le {fmtD(p.updated_at)}</div>}
          </div>
        ))}
      </div>

      {/* Maintenance : migration des images locales vers Cloudinary */}
      <div className="border border-white/10 bg-[#141414] p-4 mt-6" data-testid="image-migration">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono-hs text-[10px] uppercase tracking-widest text-[#BC5D2E] mb-1">Maintenance</div>
            <p className="text-sm text-[#A3A3A3]">Migrer les images stockées localement vers Cloudinary et mettre à jour tous les liens en base.</p>
          </div>
          <button onClick={migrateImages} disabled={migrating} className="flex items-center gap-2 border border-[#BC5D2E]/50 text-[#BC5D2E] px-4 py-2 uppercase tracking-widest text-xs hover:bg-[#BC5D2E]/10 disabled:opacity-50" data-testid="migrate-images-btn">
            <Upload size={14} /> {migrating ? "Migration…" : "Migrer vers Cloudinary"}
          </button>
        </div>
      </div>

      {creating && (
        <div className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center p-4" onClick={() => setCreating(null)}>
          <div className="w-full max-w-md bg-[#141414] border border-white/15 p-6" onClick={(e) => e.stopPropagation()} data-testid="page-create-form">
            <h3 className="font-clash text-xl uppercase tracking-tight mb-4">Nouvelle page</h3>
            <div className="space-y-4">
              <Field label="Nom de la page"><input className={inp} value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value, slug: creating.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") })} data-testid="new-page-name" /></Field>
              <Field label="Slug (URL : /p/mon-slug)"><input className={inp} value={creating.slug} onChange={(e) => setCreating({ ...creating, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} data-testid="new-page-slug" /></Field>
              <div className="flex gap-3">
                <button onClick={() => setCreating(null)} className="flex-1 border border-white/30 py-3 uppercase tracking-widest text-xs hover:border-white">Annuler</button>
                <button onClick={createPage} className="flex-1 bg-[#BC5D2E] text-white py-3 uppercase tracking-widest text-xs hover:bg-[#8A281E]" data-testid="new-page-create">Créer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Codes Promo ---------- */
function CampaignForm({ initial, expeditions, onClose, onSaved }) {
  const empty = { name: "", active: true, discount_type: "percent", discount_value: 10, expiry_date: "", max_uses: 1, once_per_client: true, expeditions: [], popup_enabled: false, code_prefix: "HS" };
  const [f, setF] = useState({ ...empty, ...(initial || {}) });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    try {
      if (initial?.id) await api.put(`/admin/promo/campaigns/${initial.id}`, f);
      else await api.post("/admin/promo/campaigns", f);
      toast.success("Campagne enregistrée ✓"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const toggleExp = (slug) => set("expeditions", f.expeditions.includes(slug) ? f.expeditions.filter((s) => s !== slug) : [...f.expeditions, slug]);
  return (
    <div className="fixed inset-0 z-[90] bg-black/70 overflow-y-auto p-4" onClick={onClose}>
      <div className="max-w-lg mx-auto bg-[#141414] border border-white/15 p-6 my-8" onClick={(e) => e.stopPropagation()} data-testid="campaign-form">
        <div className="flex justify-between mb-5"><h3 className="font-clash text-xl uppercase tracking-tight">{initial?.id ? "Modifier" : "Nouvelle"} campagne</h3><button onClick={onClose} className="text-[#A3A3A3] hover:text-white"><X /></button></div>
        <div className="space-y-4">
          <Field label="Nom"><input className={inp} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="camp-name" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Type de réduction"><select className={inp} value={f.discount_type} onChange={(e) => set("discount_type", e.target.value)} data-testid="camp-type"><option value="percent">Pourcentage (%)</option><option value="amount">Montant (€)</option></select></Field>
            <Field label="Valeur"><input type="number" className={inp} value={f.discount_value} onChange={(e) => set("discount_value", Number(e.target.value))} data-testid="camp-value" /></Field>
            <Field label="Expiration (AAAA-MM-JJ)"><input className={inp} value={f.expiry_date || ""} onChange={(e) => set("expiry_date", e.target.value)} placeholder="illimité si vide" /></Field>
            <Field label="Usages max / code (0=illimité)"><input type="number" className={inp} value={f.max_uses} onChange={(e) => set("max_uses", Number(e.target.value))} /></Field>
            <Field label="Préfixe code"><input className={inp} value={f.code_prefix} onChange={(e) => set("code_prefix", e.target.value.toUpperCase())} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#D4D4D4]"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#BC5D2E]" data-testid="camp-active" /> Campagne active</label>
          <label className="flex items-center gap-2 text-sm text-[#D4D4D4]"><input type="checkbox" checked={f.once_per_client} onChange={(e) => set("once_per_client", e.target.checked)} className="accent-[#BC5D2E]" /> Une seule utilisation par client</label>
          <label className="flex items-center gap-2 text-sm text-[#D4D4D4]"><input type="checkbox" checked={f.popup_enabled} onChange={(e) => set("popup_enabled", e.target.checked)} className="accent-[#BC5D2E]" data-testid="camp-popup" /> Utilisée par le popup du site (une seule campagne popup active)</label>
          <div>
            <span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">Expéditions concernées (aucune = toutes)</span>
            <div className="mt-2 grid grid-cols-2 gap-1 max-h-40 overflow-y-auto">
              {expeditions.map((e) => (
                <label key={e.slug} className="flex items-center gap-2 text-xs text-[#A3A3A3]"><input type="checkbox" checked={f.expeditions.includes(e.slug)} onChange={() => toggleExp(e.slug)} className="accent-[#BC5D2E]" /> {L(e.title)}</label>
              ))}
            </div>
          </div>
          <div className="flex gap-3"><button onClick={onClose} className="flex-1 border border-white/30 py-3 uppercase tracking-widest text-xs hover:border-white">Annuler</button><button onClick={save} className="flex-1 bg-[#BC5D2E] text-white py-3 uppercase tracking-widest text-xs hover:bg-[#8A281E]" data-testid="camp-save">Enregistrer</button></div>
        </div>
      </div>
    </div>
  );
}

function Promos() {
  const [camps, setCamps] = useState([]);
  const [exps, setExps] = useState([]);
  const [editing, setEditing] = useState(null);
  const [codesFor, setCodesFor] = useState(null);
  const [codes, setCodes] = useState([]);
  const load = useCallback(() => api.get("/admin/promo/campaigns").then(({ data }) => setCamps(data)).catch(() => {}), []);
  useEffect(() => { load(); api.get("/admin/expeditions").then(({ data }) => setExps(data)).catch(() => {}); }, [load]);
  const del = async (id) => { if (!window.confirm("Supprimer cette campagne ?")) return; await api.delete(`/admin/promo/campaigns/${id}`); toast.success("Supprimée"); load(); };
  const viewCodes = async (c) => { setCodesFor(c); const { data } = await api.get(`/admin/promo/campaigns/${c.id}/codes`); setCodes(data); };
  return (
    <div data-testid="admin-promos">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-clash text-2xl uppercase tracking-tight">Codes Promo</h2>
        <button onClick={() => setEditing({})} className="flex items-center gap-2 bg-[#BC5D2E] text-white px-5 py-3 uppercase tracking-widest text-xs hover:bg-[#8A281E]" data-testid="add-campaign"><Plus size={14} /> Nouvelle campagne</button>
      </div>
      <div className="space-y-3">
        {camps.length === 0 && <p className="text-[#737373] text-sm">Aucune campagne. Créez-en une pour activer le popup.</p>}
        {camps.map((c) => (
          <div key={c.id} className="border border-white/10 bg-[#141414] p-4" data-testid={`campaign-${c.id}`}>
            <div className="flex justify-between flex-wrap gap-2 items-start">
              <div>
                <div className="flex items-center gap-2"><span className="font-clash uppercase tracking-tight">{c.name}</span>
                  {c.active ? <span className="font-mono-hs text-[9px] uppercase bg-[#4ade80]/15 text-[#4ade80] border border-[#4ade80]/30 px-1.5 py-0.5">active</span> : <span className="font-mono-hs text-[9px] uppercase text-[#737373] border border-white/10 px-1.5 py-0.5">inactive</span>}
                  {c.popup_enabled && <span className="font-mono-hs text-[9px] uppercase bg-[#BC5D2E]/15 text-[#BC5D2E] border border-[#BC5D2E]/30 px-1.5 py-0.5">popup</span>}
                </div>
                <div className="font-mono-hs text-[10px] text-[#737373] uppercase tracking-widest mt-1">{c.discount_type === "percent" ? `${c.discount_value}%` : `${c.discount_value}€`} · {(c.expeditions || []).length ? `${c.expeditions.length} expé.` : "toutes expé."}{c.expiry_date ? ` · exp. ${c.expiry_date}` : ""}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => viewCodes(c)} className="text-[#A3A3A3] hover:text-white text-xs uppercase tracking-widest" data-testid={`camp-codes-${c.id}`}>Codes</button>
                <button onClick={() => setEditing(c)} className="text-[#A3A3A3] hover:text-[#BC5D2E]"><Pencil size={16} /></button>
                <button onClick={() => del(c.id)} className="text-[#A3A3A3] hover:text-[#8A281E]"><Trash2 size={16} /></button>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-3 border-t border-white/5 pt-3">
              <div><div className="font-clash text-lg text-[#F5F5F0]">{c.codes_generated}</div><div className="font-mono-hs text-[9px] uppercase text-[#737373]">générés</div></div>
              <div><div className="font-clash text-lg text-[#F5F5F0]">{c.codes_used}</div><div className="font-mono-hs text-[9px] uppercase text-[#737373]">utilisés</div></div>
              <div><div className="font-clash text-lg text-[#BC5D2E]">{eur(c.revenue)}</div><div className="font-mono-hs text-[9px] uppercase text-[#737373]">CA généré</div></div>
              <div><div className="font-clash text-lg text-[#F5F5F0]">{c.conversion}%</div><div className="font-mono-hs text-[9px] uppercase text-[#737373]">conversion</div></div>
            </div>
          </div>
        ))}
      </div>
      {editing && <CampaignForm initial={editing.id ? editing : null} expeditions={exps} onClose={() => setEditing(null)} onSaved={load} />}
      {codesFor && (
        <div className="fixed inset-0 z-[90] bg-black/70 overflow-y-auto p-4" onClick={() => setCodesFor(null)}>
          <div className="max-w-lg mx-auto bg-[#141414] border border-white/15 p-6 my-8" onClick={(e) => e.stopPropagation()} data-testid="codes-modal">
            <div className="flex justify-between mb-4"><h3 className="font-clash uppercase tracking-tight">Codes — {codesFor.name}</h3><button onClick={() => setCodesFor(null)} className="text-[#A3A3A3] hover:text-white"><X /></button></div>
            {codes.length === 0 ? <p className="text-[#737373] text-sm">Aucun code généré.</p> : (
              <div className="space-y-1">
                {codes.map((c) => (
                  <div key={c.code} className="flex justify-between items-center text-sm border-b border-white/5 py-2">
                    <span className="font-mono-hs text-[#BC5D2E]">{c.code}</span>
                    <span className="text-[#A3A3A3] text-xs truncate mx-2 flex-1">{c.first_name} · {c.email}</span>
                    <span className={`font-mono-hs text-[10px] uppercase ${(c.redemptions || []).length ? "text-[#4ade80]" : "text-[#737373]"}`}>{(c.redemptions || []).length ? "utilisé" : "envoyé"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- CMS Emails ---------- */
function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [active, setActive] = useState(null); // key
  const [form, setForm] = useState({ subject: "", heading: "", preheader: "", body: "" });
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/email-templates").then(({ data }) => {
      setTemplates(data.templates); setVariables(data.variables || []); setBlocks(data.blocks || []);
    }).catch(() => toast.error("Erreur de chargement"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (t) => {
    setActive(t.key);
    setForm({ subject: t.subject || "", heading: t.heading || "", preheader: t.preheader || "", body: t.body || "" });
    setPreviewHtml(null);
  };
  const current = templates.find((t) => t.key === active);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${active}`, form);
      toast.success("Template enregistré ✓");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const preview = async () => {
    setPreviewBusy(true);
    try {
      const { data } = await api.post(`/admin/email-templates/${active}/preview`, form);
      setPreviewHtml(data.html);
    } catch (e) { toast.error("Erreur de prévisualisation"); }
    finally { setPreviewBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Réinitialiser ce template au texte par défaut ? Vos modifications seront perdues.")) return;
    try {
      const { data } = await api.post(`/admin/email-templates/${active}/reset`);
      setForm({ subject: data.template.subject, heading: data.template.heading, preheader: data.template.preheader, body: data.template.body });
      toast.success("Réinitialisé ✓"); load();
    } catch (e) { toast.error("Erreur"); }
  };
  const insertToken = (token) => {
    setForm((f) => ({ ...f, body: `${f.body}${f.body && !f.body.endsWith("\n") ? "\n" : ""}${token}` }));
  };

  return (
    <div data-testid="admin-emails">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-clash text-2xl uppercase tracking-tight">CMS Emails</h2>
          <p className="text-[#737373] text-sm mt-1">Modifiez le contenu des emails automatiques. L'envoi utilise toujours le template enregistré en base.</p>
        </div>
      </div>

      {!active ? (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="email-template-list">
          {templates.map((t) => (
            <button key={t.key} onClick={() => openEdit(t)} data-testid={`email-tpl-${t.key}`}
              className="text-left border border-white/10 bg-[#141414] p-4 hover:border-[#BC5D2E]/50 transition-colors">
              <div className="flex items-center gap-2">
                <MailCheck size={15} className="text-[#BC5D2E]" />
                <span className="font-clash uppercase tracking-tight">{t.name}</span>
              </div>
              <div className="text-sm text-[#A3A3A3] mt-2 truncate">{t.subject}</div>
              {t.updated_at && <div className="font-mono-hs text-[9px] uppercase tracking-widest text-[#737373] mt-2">Modifié le {fmtD(t.updated_at)}</div>}
            </button>
          ))}
        </div>
      ) : (
        <div data-testid="email-editor">
          <button onClick={() => { setActive(null); setPreviewHtml(null); }} className="text-[#A3A3A3] hover:text-white text-xs uppercase tracking-widest mb-4 flex items-center gap-1" data-testid="email-back">← Retour à la liste</button>
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Éditeur */}
            <div className="space-y-4">
              <h3 className="font-clash text-lg uppercase tracking-tight">{current?.name}</h3>
              <Field label="Objet de l'email"><input className={inp} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="email-subject" /></Field>
              <Field label="Titre (grand titre en haut)"><input className={inp} value={form.heading} onChange={(e) => setForm({ ...form, heading: e.target.value })} data-testid="email-heading" /></Field>
              <Field label="Pré-en-tête (aperçu boîte mail)"><input className={inp} value={form.preheader} onChange={(e) => setForm({ ...form, preheader: e.target.value })} data-testid="email-preheader" /></Field>
              <Field label="Corps du message (HTML + variables)">
                <textarea className={`${inp} font-mono-hs text-xs leading-relaxed`} rows={14} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="email-body" />
              </Field>
              <div className="border border-white/10 p-3 bg-[#0F0F0F]">
                <div className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] mb-2">Blocs dynamiques (cliquez pour insérer)</div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {blocks.map(([token, desc]) => (
                    <button key={token} title={desc} onClick={() => insertToken(token)} className="font-mono-hs text-[10px] border border-[#BC5D2E]/40 text-[#BC5D2E] px-2 py-1 hover:bg-[#BC5D2E]/10" data-testid={`block-${token}`}>{token}</button>
                  ))}
                </div>
                <div className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] mb-2">Variables (cliquez pour insérer)</div>
                <div className="flex flex-wrap gap-1.5">
                  {variables.map(([token, desc]) => (
                    <button key={token} title={desc} onClick={() => insertToken(token)} data-testid={`var-${token}`} className="font-mono-hs text-[10px] border border-white/15 text-[#A3A3A3] px-2 py-1 hover:border-white hover:text-white">{token}</button>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={save} disabled={saving} className="flex-1 bg-[#BC5D2E] text-white py-3 uppercase tracking-widest text-xs hover:bg-[#8A281E] disabled:opacity-50" data-testid="email-save">{saving ? "Enregistrement…" : "Enregistrer"}</button>
                <button onClick={preview} disabled={previewBusy} className="flex items-center gap-1 border border-white/20 px-4 py-3 uppercase tracking-widest text-xs hover:border-white" data-testid="email-preview-btn"><Eye size={13} /> Prévisualiser</button>
                <button onClick={reset} className="flex items-center gap-1 border border-white/20 px-4 py-3 uppercase tracking-widest text-xs text-[#A3A3A3] hover:text-white hover:border-white" data-testid="email-reset"><RotateCcw size={13} /> Défaut</button>
              </div>
            </div>
            {/* Prévisualisation */}
            <div>
              <div className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] mb-2">Prévisualisation (données d'exemple)</div>
              {previewHtml ? (
                <iframe title="preview" srcDoc={previewHtml} className="w-full h-[640px] border border-white/10 bg-white" data-testid="email-preview-frame" />
              ) : (
                <div className="w-full h-[640px] border border-dashed border-white/15 flex items-center justify-center text-[#737373] text-sm">
                  Cliquez sur « Prévisualiser » pour voir le rendu.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Coordinateurs / Guides ---------------- */
function PhotoUpload({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const up = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url); toast.success("Photo ajoutée ✓");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'upload"); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-4">
      {value
        ? <img src={imgSrc(value)} alt="" className="w-20 h-20 object-cover rounded-full border border-[#BC5D2E]/50" />
        : <div className="w-20 h-20 rounded-full border border-dashed border-white/20 flex items-center justify-center text-[#555]"><ImagePlus size={18} /></div>}
      <label className="cursor-pointer border border-white/20 px-3 py-2 text-xs uppercase tracking-widest hover:border-[#BC5D2E] text-[#A3A3A3]" data-testid="coord-photo-label">
        {busy ? "Envoi…" : (value ? "Changer la photo" : "Ajouter une photo")}
        <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid="coord-photo-input"
               onChange={(e) => { if (e.target.files?.[0]) up(e.target.files[0]); e.target.value = ""; }} />
      </label>
      {value && <button type="button" onClick={() => onChange("")} className="text-[#666] hover:text-[#8A281E]"><Trash2 size={16} /></button>}
    </div>
  );
}

function CoordinatorForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", photo: "", role: "", guide_since_year: "", languages: [], specialties: [], bio: "", order: 0, ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name?.trim()) { toast.error("Le nom est requis."); return; }
    setBusy(true);
    try {
      const payload = { ...f, guide_since_year: f.guide_since_year ? Number(f.guide_since_year) : null };
      if (initial?.id) await api.put(`/admin/coordinators/${initial.id}`, payload);
      else await api.post("/admin/coordinators", payload);
      toast.success("Enregistré ✓"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[90] bg-black/70 overflow-y-auto p-4" onClick={onClose}>
      <div className="max-w-xl mx-auto bg-[#141414] border border-white/15 p-6 my-8" onClick={(e) => e.stopPropagation()} data-testid="coordinator-form">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-clash text-xl uppercase tracking-tight">{initial?.id ? "Modifier" : "Nouveau"} coordinateur</h3>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white" data-testid="coord-form-close"><X /></button>
        </div>
        <div className="space-y-5">
          <PhotoUpload value={f.photo} onChange={(v) => set("photo", v)} />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nom"><input className={inp} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="coord-name" /></Field>
            <Field label="Rôle / fonction"><input className={inp} value={f.role} onChange={(e) => set("role", e.target.value)} placeholder="Guide expédition" data-testid="coord-role" /></Field>
            <Field label="Guide depuis (année)"><input type="number" className={inp} value={f.guide_since_year || ""} onChange={(e) => set("guide_since_year", e.target.value)} placeholder="2015" data-testid="coord-since" /></Field>
            <Field label="Ordre d'affichage"><input type="number" className={inp} value={f.order} onChange={(e) => set("order", Number(e.target.value))} /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <BlockList label="Langues pratiquées" items={f.languages} onChange={(v) => set("languages", v)} testid="coord-languages" />
            <BlockList label="Spécialités / terrains" items={f.specialties} onChange={(v) => set("specialties", v)} testid="coord-specialties" />
          </div>
          <Field label="Courte bio (2-3 lignes)"><textarea rows={3} className={inp} value={f.bio} onChange={(e) => set("bio", e.target.value)} data-testid="coord-bio" /></Field>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 border border-white/30 py-3 uppercase tracking-widest text-xs hover:border-white">Annuler</button>
          <button onClick={save} disabled={busy} className="flex-1 bg-[#BC5D2E] text-white py-3 uppercase tracking-widest text-xs hover:bg-[#8A281E] disabled:opacity-50" data-testid="coord-save">{busy ? "..." : "Enregistrer"}</button>
        </div>
      </div>
    </div>
  );
}

function Coordinators() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/coordinators").then(({ data }) => setList(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const del = async (c) => { if (!window.confirm(`Supprimer ${c.name} ?`)) return; await api.delete(`/admin/coordinators/${c.id}`); toast.success("Supprimé"); load(); };
  return (
    <div data-testid="admin-coordinators">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-clash text-2xl uppercase tracking-tight">Coordinateurs &amp; Guides</h2>
          <p className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] mt-1">Le côté humain — reliés aux expéditions</p>
        </div>
        <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-[#BC5D2E] text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#8A281E]" data-testid="coord-new"><Plus size={16} /> Nouveau</button>
      </div>
      {list.length === 0 && <p className="text-[#737373] font-mono-hs text-sm">Aucun coordinateur. Ajoutez-en un pour l'afficher sur les fiches d'expédition.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((c) => (
          <div key={c.id} className="border border-white/10 bg-[#141414] p-4 flex gap-4" data-testid={`coord-card-${c.id}`}>
            {c.photo
              ? <img src={imgSrc(c.photo)} alt={c.name} className="w-16 h-16 object-cover rounded-full border border-[#BC5D2E]/40 shrink-0" />
              : <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center text-[#555] shrink-0"><Compass size={20} /></div>}
            <div className="min-w-0 flex-1">
              <div className="font-clash text-lg leading-tight truncate">{c.name}</div>
              <div className="font-mono-hs text-[10px] uppercase tracking-widest text-[#BC5D2E]">{c.role || "—"}</div>
              {c.guide_since_year ? <div className="text-xs text-[#A3A3A3] mt-1">Guide depuis {c.guide_since_year}</div> : null}
              {c.languages?.length ? <div className="text-xs text-[#737373] mt-1 truncate">{c.languages.join(" · ")}</div> : null}
              <div className="flex gap-2 mt-2">
                <button onClick={() => setEditing(c)} className="text-[#A3A3A3] hover:text-white" data-testid={`coord-edit-${c.id}`}><Pencil size={14} /></button>
                <button onClick={() => del(c)} className="text-[#A3A3A3] hover:text-[#8A281E]" data-testid={`coord-del-${c.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && <CoordinatorForm initial={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

const NAV = [
  { id: "dashboard", label: "Tableau de bord", icon: LayoutDashboard },
  { id: "expeditions", label: "Expéditions", icon: Mountain },
  { id: "coordinators", label: "Coordinateurs", icon: Compass },
  { id: "reservations", label: "Réservations", icon: CalendarCheck },
  { id: "promos", label: "Codes Promo", icon: Ticket },
  { id: "clients", label: "Clients", icon: Users },
  { id: "contacts", label: "Contacts & Leads", icon: Mail },
  { id: "emails", label: "Emails", icon: MailCheck },
  { id: "pages", label: "Pages du site", icon: FileText },
];

export default function Admin() {
  const { admin, loading, logout } = useAdmin();
  const [section, setSection] = useState("dashboard");
  useEffect(() => { document.title = "ADMIN - Hors Signal"; }, []);
  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center font-mono-hs text-[#BC5D2E]">// ...</div>;
  if (!admin) return <AdminLogin />;
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0] flex flex-col md:flex-row">
      <aside className="md:w-64 md:min-h-screen border-b md:border-b-0 md:border-r border-white/10 p-5 flex md:flex-col gap-2 md:gap-1 overflow-x-auto">
        <div className="font-clash font-bold text-xl uppercase tracking-tight mb-4 hidden md:block">Hors<span className="text-[#BC5D2E]">Signal</span><div className="font-mono-hs text-[9px] text-[#737373] tracking-widest">BACK-OFFICE</div></div>
        {NAV.map((n) => (
          <button key={n.id} onClick={() => setSection(n.id)} data-testid={`nav-${n.id}`}
            className={`flex items-center gap-3 px-4 py-3 text-xs uppercase tracking-widest whitespace-nowrap transition-colors ${section === n.id ? "bg-[#BC5D2E] text-white" : "text-[#A3A3A3] hover:text-white hover:bg-white/5"}`}>
            <n.icon size={16} /> <span className="hidden sm:inline">{n.label}</span>
          </button>
        ))}
        <div className="md:mt-auto flex gap-2">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-white"><ExternalLink size={16} /><span className="hidden sm:inline">Voir le site</span></a>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-[#8A281E]"><LogOut size={16} /><span className="hidden sm:inline">Quitter</span></button>
        </div>
      </aside>
      <main className="flex-1 p-6 sm:p-10 overflow-x-hidden">
        {section === "dashboard" && <Dashboard go={setSection} />}
        {section === "expeditions" && <Expeditions />}
        {section === "coordinators" && <Coordinators />}
        {section === "reservations" && <Reservations />}
        {section === "promos" && <Promos />}
        {section === "clients" && <Clients />}
        {section === "contacts" && <Contacts />}
        {section === "emails" && <EmailTemplates />}
        {section === "pages" && <Pages />}
      </main>
    </div>
  );
}
